import { Row, Col, Input, Button as AntButton, Form, Spin } from "antd";
import axiosV1 from "@lhd/libs/axios";
import { FC, useEffect, useState } from "react";
import moment, { Moment } from "moment";
import "moment/locale/ja";
import {
  BUTTON_HEIGHT_LARGE,
  DATE_TIME_FORMAT,
  FONT_SIZE,
  FONT_SIZE_VALUE,
  SERVICES,
  VIEW_MODE_OPTIONS,
} from "@lhd/constants";
import { CommonDateFormPicker } from "@lhd/components/CommonDatePicker";
import { notification } from "antd";
import { useSubmitFormState } from "@lhd/components/FormState/hook";
import { ROUTES } from "@lhd/constants";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BiSave } from "react-icons/bi";
import { RiArrowGoBackFill } from "react-icons/ri";
import WrapperDetail from "@lhd/components/Wrapper";
import { StyleMaintenanceNotificationDetail, StylesModalForm } from "./styled";
import { getCompanyId } from "@lhd/utility";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { DISPLAY_SIZES, MODE } from "@lhd/types";
import ButtonCommon from "@lhd/components/ButtonCommon";
import { useControlStatement } from "@lhd/hooks";

const { TextArea } = Input;

interface IProps {
  handleCancel?: () => void;
  isOpen?: boolean;
  onSuccess?: () => void;
  selectedId?: string;
  pageMode?: "create" | "view" | "edit";
  isQuickViewMode?: boolean;
}

interface FormValues {
  title: string;
  main_notice: string;
  advance_notice: string;
  completion_notice: string;
  maintenance_start_at: Moment | null | undefined;
  maintenance_end_at: Moment | null | undefined;
  advance_start_at: Moment | null | undefined;
  completion_end_at: Moment | null | undefined;
}

const ModalForm: FC<IProps> = ({
  handleCancel,
  isOpen,
  onSuccess,
  selectedId,
  pageMode: pageModeProp,
  isQuickViewMode = false,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [isShowMain, setIsShowMain] = useState(true);
  const [isShowAdvance, setIsShowAdvance] = useState(true);
  const [isShowCompletion, setIsShowCompletion] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>();
  const { id } = useParams();
  const navigate = useNavigate();
  const idNoti = id || selectedId;

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT_LARGE;
  const height = BUTTON_HEIGHT_LARGE[displaySize] ?? BUTTON_HEIGHT_LARGE.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  const { elementSegmentedControl, value: mode } = useControlStatement({
    options: VIEW_MODE_OPTIONS,
    displaySize,
    defaultValue: pageModeProp || MODE.VIEW,
    disabled: !data?.editable,
  });

  const {
    getButtonStyle,
    registerFormWatcher,
    markSubmit,
    getFieldClassName,
    unMarkSubmit,
    checkState,
    buttonState,
  } = useSubmitFormState(form, mode === "edit", [
    "title",
    "main_notice",
    "maintenance_start_at",
    "maintenance_end_at",
  ]);

  useEffect(() => {
    if (data && (mode === "view" || mode === "edit")) {
      form.setFieldsValue({
        title: data.title,
        main_notice: data.main_notice?.replace(/[\r]+/g, "").trim(),
        advance_notice: data.advance_notice?.replace(/[\r]+/g, "").trim(),
        completion_notice: data.completion_notice?.replace(/[\r]+/g, "").trim(),
        maintenance_start_at: data.maintenance_start_at
          ? moment(data.maintenance_start_at)
          : null,
        maintenance_end_at: data.maintenance_end_at
          ? moment(data.maintenance_end_at)
          : null,
        advance_start_at: data.advance_start_at
          ? moment(data.advance_start_at)
          : null,
        completion_end_at: data.completion_end_at
          ? moment(data.completion_end_at)
          : null,
      });
      setIsShowMain(data.is_show_main_notice);
      setIsShowAdvance(data.is_show_advance_notice);
      setIsShowCompletion(data.is_show_completion_notice);
    } else {
      form.resetFields();
      setIsShowMain(true);
      setIsShowAdvance(true);
      setIsShowCompletion(true);
    }
  }, [data, mode, form]);

  const close = () => {
    handleCancel?.();
    unMarkSubmit();
  };

  useEffect(() => {
    if (mode === "edit") {
      checkState();
    }
  }, [mode]);

  const main_notice = Form.useWatch("main_notice", form) || "";
  const advance_notice = Form.useWatch("advance_notice", form) || "";
  const completion_notice = Form.useWatch("completion_notice", form) || "";
  const advance_start_at = Form.useWatch("advance_start_at", form);
  const completion_end_at = Form.useWatch("completion_end_at", form);

  const disabledPastDate = (current: Moment): boolean => {
    return current && current.isBefore(moment(), "day");
  };

  const disabledEndDateTime = (current: Moment): boolean => {
    const startDateTime = form.getFieldValue("maintenance_start_at");
    if (!current || !startDateTime) {
      return current && current.isBefore(moment(), "day");
    }
    return current && current.isBefore(startDateTime, "day");
  };

  const disabledStartTime = (current: Moment): boolean => {
    const startDateTime = form.getFieldValue("maintenance_start_at");
    if (!current) {
      return true;
    }
    if (current.isBefore(moment(), "day")) {
      return true;
    }
    if (startDateTime && current.isAfter(startDateTime, "day")) {
      return true;
    }
    return false;
  };

  const disabledEndTime = (current: Moment): boolean => {
    const endDateTime = form.getFieldValue("maintenance_end_at");
    if (!current || !endDateTime) {
      return current && current.isBefore(moment(), "day");
    }
    return current && current.isBefore(endDateTime, "day");
  };

  const isMaintenanceStartDisabled =
    mode === "view" ||
    (mode === "edit" &&
      data?.maintenance_start_at &&
      moment(data.maintenance_start_at).isBefore(moment()));
  const isMaintenanceEndDisabled =
    mode === "view" ||
    (mode === "edit" &&
      data?.maintenance_end_at &&
      moment(data.maintenance_end_at).isBefore(moment()));
  const isAdvanceStartDisabled =
    mode === "view" ||
    (mode === "edit" &&
      data?.advance_start_at &&
      moment(data.advance_start_at).isBefore(moment()));
  const isCompletionEndDisabled = mode === "view";

  const handleAdvanceStartChange = (value: Moment | null) => {
    const startDateTime = form.getFieldValue("maintenance_start_at");
    const currentTime = moment();
    if (!value) {
      form.setFields([
        {
          name: "advance_start_at",
          value,
          errors: [],
        },
      ]);
      return;
    }
    if (
      startDateTime &&
      value.isSameOrAfter(startDateTime, "minute") &&
      !isAdvanceStartDisabled
    ) {
      form.setFields([
        {
          name: "advance_start_at",
          value: null,
          errors: ["開始時間は開始日時より前または同じでなければなりません"],
        },
      ]);
      return;
    }
    if (value.isSame(currentTime, "day") && value.isBefore(currentTime)) {
      form.setFields([
        {
          name: "advance_start_at",
          value: null,
          errors: ["開始時間は現在より後の時間でなければなりません"],
        },
      ]);
      return;
    }
    form.setFields([
      {
        name: "advance_start_at",
        value,
        errors: [],
      },
    ]);
  };

  const customTimeConfig = (selectedDate: Moment | null) => {
    const currentTime = moment();
    let defaultMinute = 0;

    if (selectedDate && selectedDate.isSame(currentTime, "day")) {
      const currentMinute = currentTime.minute();
      defaultMinute = currentMinute < 30 ? 0 : 30;
    }

    return {
      format: "HH:mm",
      hideDisabledOptions: true,
      defaultValue: selectedDate
        ? selectedDate.clone().minute(defaultMinute)
        : currentTime.clone().minute(defaultMinute),
      disabledMinutes: () => {
        const disabled = [];
        for (let i = 0; i < 60; i++) {
          if (i !== 0 && i !== 30) {
            disabled.push(i);
          }
        }
        return disabled;
      },
      disabledHours: () => {
        if (selectedDate && selectedDate.isSame(currentTime, "day")) {
          const currentHour = currentTime.hour();
          return Array.from({ length: currentHour }, (_, i) => i);
        }
        return [];
      },
    };
  };

  const handleDateChange = (
    value: Moment | null,
    field: string,
    validator?: (value: Moment) => boolean,
  ) => {
    if (!value) {
      form.setFields([{ name: field, value, errors: [] }]);
      return;
    }

    const minute = value.minute();
    const roundDown = value
      .clone()
      .second(0)
      .millisecond(0)
      .minute(minute < 30 ? 0 : 30);
    // If rounding down makes the time invalid (e.g. past), round up to next slot.
    const roundUp =
      minute < 30
        ? value.clone().second(0).millisecond(0).minute(30)
        : value.clone().second(0).millisecond(0).add(1, "hour").minute(0);

    let adjustedValue = roundDown;
    if (validator && validator(adjustedValue)) {
      adjustedValue = roundUp;
    }

    if (validator && validator(adjustedValue)) {
      form.setFields([
        {
          name: field,
          value: null,
          errors: ["無効な時間です"],
        },
      ]);
      return;
    }

    form.setFields([
      {
        name: field,
        value: adjustedValue,
        errors: [],
      },
    ]);
  };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    const bodyFormData = new FormData();
    bodyFormData.append("title", values.title?.trim());
    bodyFormData.append("main_notice", values.main_notice?.trim());
    bodyFormData.append("advance_notice", values.advance_notice?.trim() || "");
    bodyFormData.append(
      "completion_notice",
      values.completion_notice?.trim() || "",
    );
    if (values.maintenance_start_at) {
      bodyFormData.append(
        "maintenance_start_at",
        values.maintenance_start_at.toISOString(),
      );
    }
    if (values.maintenance_end_at) {
      bodyFormData.append(
        "maintenance_end_at",
        values.maintenance_end_at.toISOString(),
      );
    }
    bodyFormData.append(
      "advance_start_at",
      values.advance_start_at ? values.advance_start_at.toISOString() : "",
    );
    bodyFormData.append(
      "completion_end_at",
      values.completion_end_at ? values.completion_end_at.toISOString() : "",
    );
    bodyFormData.append("is_show_main_notice", isShowMain.toString());
    bodyFormData.append("is_show_advance_notice", isShowAdvance.toString());
    bodyFormData.append(
      "is_show_completion_notice",
      isShowCompletion.toString(),
    );
    bodyFormData.append("current_company_id", getCompanyId().toString());
    try {
      try {
        await axiosV1({
          method: mode === "create" ? "post" : "patch",
          url:
            mode === "create"
              ? SERVICES.MAINTENANCE_NOTICES
              : `${SERVICES.MAINTENANCE_NOTICES}/${id}`,
          data: bodyFormData,
          params: {
            current_company_id: getCompanyId(),
          },
        });
      } catch (error: any) {
        notification["error"]({
          message: error?.response?.data?.error?.[0] || "",
        });
        close();
        return;
      }
      if (mode === "create") {
        navigate(`/${ROUTES.MAINTENANCE_NOTIFICATION}`);
      }
      onSuccess?.();
      notification["success"]({
        message: "保存しました",
      });
      close();
    } catch (e) {
      console.error(e);
      close();
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = mode === "view";

  const handleViewDetail = async () => {
    if (!idNoti) return;
    try {
      setIsLoading(true);
      const response = await axiosV1({
        method: "get",
        url: `${SERVICES.MAINTENANCE_NOTICES}/${idNoti}`,
        params: {
          current_company_id: getCompanyId(),
        },
      });
      setData(response.data);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  };
  useEffect(() => {
    handleViewDetail();
  }, [idNoti]);

  return (
    <StyleMaintenanceNotificationDetail>
      <WrapperDetail
        isShowQuickView={id || mode === "create" ? false : true}
        isOpen={isOpen || false}
        onClose={close}
        handleViewDetail={() =>
          window.open(
            `/${ROUTES.MAINTENANCE_NOTIFICATION}/${selectedId}?pageMode=view&isDisplayInNewTab=true`,
            "_blank",
          )
        }
        handleViewEdit={() =>
          window.open(
            `/${ROUTES.MAINTENANCE_NOTIFICATION}/${selectedId}?pageMode=edit&isDisplayInNewTab=true`,
            "_blank",
          )
        }
        btnGroup={
          <>
            {mode !== "create" && !isQuickViewMode && elementSegmentedControl}
            {mode === "create" && (
              <ButtonCommon
                icon={
                  <div className="flex-center">
                    <RiArrowGoBackFill size={16} />
                  </div>
                }
                className="btn-back"
                onClick={() => navigate(`/${ROUTES.MAINTENANCE_NOTIFICATION}`)}
                name="戻る"
                disabled={false}
                style={{
                  fontSize: size,
                  height: `${height}px`,
                  width: "fit-content",
                  gap: 5,
                }}
              />
            )}

            <ButtonCommon
              name="保存"
              icon={
                <div className="flex-center">
                  <BiSave size={18} />
                </div>
              }
              className={`btn-submit ${isDisabled ? "btn-disabled" : ""}`}
              disabled={isDisabled || buttonState === "default"}
              onClick={() => {
                form.submit();
              }}
              style={{
                ...getButtonStyle(),
                fontSize: size,
                height: `${height}px`,
                width: "fit-content",
                gap: 5,
              }}
            />
          </>
        }
        disabledBtnEdit={!data?.editable}
        containerHeight={"50vh"}
        quickButtonDirection="vertical"
      >
        <StylesModalForm
          text_scale={displaySize}
          style={{ paddingTop: isQuickViewMode ? 10 : 0 }}
        >
          <Spin spinning={isLoading}>
            <Form
              form={form}
              onFinish={handleSubmit}
              onFinishFailed={markSubmit}
              layout="vertical"
              {...registerFormWatcher()}
            >
              <Form.Item
                label="タイトル"
                name="title"
                rules={[
                  {
                    required: true,
                    message: "必須項目です",
                  },
                  {
                    validator: (_, value: string) => {
                      if (!value) return Promise.resolve();
                      if (value.trim() === "" && !isDisabled) {
                        return Promise.reject("タイトルを入力してください");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                className={`${
                  mode == "view" ? "" : getFieldClassName("title")
                }`}
                validateTrigger="onBlur"
              >
                <Input
                  placeholder="2025/05/16のメンテナンスのお知らせ"
                  disabled={isDisabled}
                />
              </Form.Item>
              <Row gutter={24}>
                <Col xs={24} md={16}>
                  <div className="form-group">
                    <div className="label">
                      <div className="text">
                        <span className="required">*</span>
                        ※メンテナンス中のお知らせ
                      </div>
                      {!isDisabled && (
                        <div
                          className={
                            isShowMain ? "btn-label-show" : "btn-label"
                          }
                          onClick={() => setIsShowMain((prev) => !prev)}
                          style={{ cursor: "pointer" }}
                        >
                          {isShowMain ? "非表示" : "表示"}
                        </div>
                      )}
                    </div>
                    <Form.Item
                      name="main_notice"
                      rules={[
                        {
                          validator: (_, value: string) => {
                            if (
                              (!value || value.trim() === "") &&
                              !isDisabled
                            ) {
                              return Promise.reject("必須項目です");
                            }
                            return Promise.resolve();
                          },
                        },
                        { max: 200, message: "200文字以内にしてください" },
                      ]}
                      className={`${
                        mode == "view" ? "" : getFieldClassName("main_notice")
                      }`}
                      validateTrigger="onBlur"
                    >
                      <TextArea
                        rows={5}
                        placeholder="アップデート中（実施時間：5/16（金）17:00～20:00）..."
                        maxLength={200}
                        disabled={isDisabled}
                      />
                    </Form.Item>
                    <div className="char-count">{main_notice.length} / 200</div>
                  </div>
                  <div className="form-group">
                    <div className="label">
                      <div className="text">メンテナンスの事前告知</div>
                      {!isDisabled && (
                        <div
                          className={
                            isShowAdvance ? "btn-label-show" : "btn-label"
                          }
                          onClick={() => setIsShowAdvance((prev) => !prev)}
                          style={{ cursor: "pointer" }}
                        >
                          {isShowAdvance ? "非表示" : "表示"}
                        </div>
                      )}
                    </div>
                    <Form.Item
                      name="advance_notice"
                      dependencies={["advance_start_at"]}
                      rules={[
                        {
                          validator: (_, value: string) => {
                            if (!isDisabled) {
                              if (
                                advance_start_at &&
                                !isAdvanceStartDisabled &&
                                !isMaintenanceStartDisabled &&
                                (!value || value.trim() === "")
                              ) {
                                return Promise.reject(
                                  "メンテナンスの事前告知の開始時間が入力されていますが、事前告知内容が入力されていません。",
                                );
                              }
                              if (
                                value &&
                                value.trim() !== "" &&
                                !advance_start_at
                              ) {
                                return Promise.reject(
                                  "メンテナンスの事前告知が入力されていますが、開始時間が入力されていません。",
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                        { max: 200, message: "200文字以内にしてください" },
                      ]}
                    >
                      <TextArea
                        rows={5}
                        placeholder="メンテナンスの事前告知（実施時間：5/16（金）17:00～20:00）..."
                        maxLength={200}
                        disabled={isDisabled}
                        onChange={() => {
                          form.validateFields(["advance_start_at"]);
                        }}
                      />
                    </Form.Item>
                    <div className="char-count">
                      {advance_notice.length} / 200
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="label">
                      <div className="text">メンテナンス完了のお知らせ</div>
                      {!isDisabled && (
                        <div
                          className={
                            isShowCompletion ? "btn-label-show" : "btn-label"
                          }
                          onClick={() => setIsShowCompletion((prev) => !prev)}
                          style={{ cursor: "pointer" }}
                        >
                          {isShowCompletion ? "非表示" : "表示"}
                        </div>
                      )}
                    </div>
                    <Form.Item
                      name="completion_notice"
                      dependencies={["completion_end_at"]}
                      rules={[
                        {
                          validator: (_, value: string) => {
                            if (!isDisabled) {
                              if (
                                completion_end_at &&
                                !isCompletionEndDisabled &&
                                !isMaintenanceEndDisabled &&
                                (!value || value.trim() === "")
                              ) {
                                return Promise.reject(
                                  "メンテナンス完了のお知らせの終了時間が入力されていますが、お知らせ内容が入力されていません。",
                                );
                              }
                              if (
                                value &&
                                value.trim() !== "" &&
                                !completion_end_at
                              ) {
                                return Promise.reject(
                                  "メンテナンス完了のお知らせ内容が入力されていますが、終了時間が入力されていません。",
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                        { max: 200, message: "200文字以内にしてください" },
                      ]}
                    >
                      <TextArea
                        rows={5}
                        placeholder="メンテナンスは予定通り完了しました..."
                        maxLength={200}
                        disabled={isDisabled}
                        onChange={() => {
                          form.validateFields(["completion_end_at"]);
                        }}
                      />
                    </Form.Item>
                    <div className="char-count">
                      {completion_notice.length} / 200
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="maintenance_start_at"
                    label="開始日時"
                    rules={
                      !isMaintenanceStartDisabled
                        ? [
                            {
                              required: true,
                              message: "必須項目です",
                            },
                            {
                              validator: (_, value: Moment) => {
                                if (!value) {
                                  return Promise.resolve();
                                }
                                if (value.isSame(moment(), "day")) {
                                  if (value.isBefore(moment())) {
                                    return Promise.reject(
                                      "開始日時は現在より後の時間でなければなりません",
                                    );
                                  }
                                } else if (value.isBefore(moment(), "day")) {
                                  return Promise.reject(
                                    "開始日時は過去の日付を選択できません",
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]
                        : []
                    }
                    className={`${
                      mode == "view"
                        ? ""
                        : getFieldClassName("maintenance_start_at")
                    }`}
                    validateTrigger="onBlur"
                  >
                    <CommonDateFormPicker
                      placeholder=""
                      displayFormat={DATE_TIME_FORMAT.DATE_JP_TIME}
                      showTime={customTimeConfig(
                        form.getFieldValue("maintenance_start_at"),
                      )}
                      disabledDate={
                        isMaintenanceStartDisabled
                          ? () => true
                          : disabledPastDate
                      }
                      disabled={isMaintenanceStartDisabled}
                      allowClear
                      onChange={(value) => {
                        handleDateChange(
                          value,
                          "maintenance_start_at",
                          (val) =>
                            val.isSame(moment(), "day") &&
                            val.isBefore(moment()),
                        );
                        form.validateFields(["advance_start_at"]);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    className={`mt-5 ${
                      mode == "view"
                        ? ""
                        : getFieldClassName("maintenance_end_at")
                    }`}
                    name="maintenance_end_at"
                    label="終了日時"
                    rules={
                      !isMaintenanceEndDisabled
                        ? [
                            {
                              required: true,
                              message: "必須項目です",
                            },
                            {
                              validator: (_, value: Moment) => {
                                if (!value) {
                                  return Promise.resolve();
                                }
                                const startDateTime = form.getFieldValue(
                                  "maintenance_start_at",
                                );
                                if (value.isSame(moment(), "day")) {
                                  if (value.isBefore(moment())) {
                                    return Promise.reject(
                                      "終了日時は現在より後の時間でなければなりません",
                                    );
                                  }
                                } else if (value.isBefore(moment(), "day")) {
                                  return Promise.reject(
                                    "終了日時は過去の日付を選択できません",
                                  );
                                }
                                if (
                                  value &&
                                  startDateTime &&
                                  value.isSameOrBefore(startDateTime, "minute")
                                ) {
                                  return Promise.reject(
                                    "終了日時は開始日時より後でなければなりません",
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]
                        : []
                    }
                    validateTrigger="onBlur"
                  >
                    <CommonDateFormPicker
                      placeholder=""
                      displayFormat={DATE_TIME_FORMAT.DATE_JP_TIME}
                      showTime={customTimeConfig(
                        form.getFieldValue("maintenance_end_at"),
                      )}
                      disabledDate={
                        isMaintenanceEndDisabled
                          ? () => true
                          : disabledEndDateTime
                      }
                      disabled={isMaintenanceEndDisabled}
                      allowClear
                      onChange={(value) =>
                        handleDateChange(value, "maintenance_end_at", (val) => {
                          const startDateTime = form.getFieldValue(
                            "maintenance_start_at",
                          );
                          return (
                            (val.isSame(moment(), "day") &&
                              val.isBefore(moment())) ||
                            (val &&
                              startDateTime &&
                              val.isSameOrBefore(startDateTime, "minute"))
                          );
                        })
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="advance_start_at"
                    label="開始時間"
                    rules={
                      !isAdvanceStartDisabled
                        ? [
                            {
                              validator: (_, value: Moment) => {
                                if (!value) {
                                  if (
                                    advance_notice &&
                                    advance_notice.trim() !== ""
                                  ) {
                                    return Promise.reject(
                                      "メンテナンスの事前告知が入力されていますが、開始時間が入力されていません。",
                                    );
                                  }
                                  return Promise.resolve();
                                }
                                const startDateTime = form.getFieldValue(
                                  "maintenance_start_at",
                                );
                                if (value.isSame(moment(), "day")) {
                                  if (value.isBefore(moment())) {
                                    return Promise.reject(
                                      "開始時間は現在より後の時間でなければなりません",
                                    );
                                  }
                                } else if (value.isBefore(moment(), "day")) {
                                  return Promise.reject(
                                    "開始時間は過去の日付を選択できません",
                                  );
                                }
                                if (value && startDateTime) {
                                  if (
                                    value.isSameOrAfter(startDateTime, "minute")
                                  ) {
                                    return Promise.reject(
                                      "開始日時がメンテナンス中のお知らせの開始日時の前に設定してください。",
                                    );
                                  }
                                }
                                return Promise.resolve();
                              },
                            },
                          ]
                        : []
                    }
                    style={{
                      marginTop:
                        mode === "edit"
                          ? displaySize === DISPLAY_SIZES.SMALL
                            ? 4
                            : displaySize === DISPLAY_SIZES.MEDIUM
                            ? 34
                            : 57
                          : displaySize === DISPLAY_SIZES.SMALL
                          ? -5
                          : displaySize === DISPLAY_SIZES.MEDIUM
                          ? 34
                          : 57,
                    }}
                  >
                    <CommonDateFormPicker
                      placeholder=""
                      displayFormat={DATE_TIME_FORMAT.DATE_JP_TIME}
                      showTime={customTimeConfig(
                        form.getFieldValue("advance_start_at"),
                      )}
                      disabledDate={
                        isAdvanceStartDisabled || isMaintenanceStartDisabled
                          ? () => true
                          : disabledStartTime
                      }
                      onChange={handleAdvanceStartChange}
                      allowClear
                      disabled={
                        isAdvanceStartDisabled || isMaintenanceStartDisabled
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="completion_end_at"
                    label="終了時間"
                    rules={
                      !isCompletionEndDisabled
                        ? [
                            {
                              validator: (_, value: Moment) => {
                                if (!value) {
                                  if (
                                    completion_notice &&
                                    completion_notice.trim() !== ""
                                  ) {
                                    return Promise.reject(
                                      "メンテナンス完了のお知らせ内容が入力されていますが、終了時間が入力されていません。",
                                    );
                                  }
                                  return Promise.resolve();
                                }
                                const endDateTime =
                                  form.getFieldValue("maintenance_end_at");
                                if (value.isSame(moment(), "day")) {
                                  if (value.isBefore(moment())) {
                                    return Promise.reject(
                                      "終了時間は現在より後の時間でなければなりません",
                                    );
                                  }
                                } else if (value.isBefore(moment(), "day")) {
                                  return Promise.reject(
                                    "終了時間は過去の日付を選択できません",
                                  );
                                }
                                if (
                                  value &&
                                  endDateTime &&
                                  value.isSameOrBefore(endDateTime, "minute")
                                ) {
                                  return Promise.reject(
                                    "終了日時がメンテナンス中のお知らせの終了日時の後に設定してください。",
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]
                        : []
                    }
                    style={{
                      marginTop:
                        mode === "edit"
                          ? displaySize === DISPLAY_SIZES.SMALL
                            ? 121
                            : displaySize === DISPLAY_SIZES.MEDIUM
                            ? 134
                            : 157
                          : displaySize === DISPLAY_SIZES.SMALL
                          ? 118
                          : displaySize === DISPLAY_SIZES.MEDIUM
                          ? 136
                          : 158,
                    }}
                  >
                    <CommonDateFormPicker
                      placeholder=""
                      displayFormat={DATE_TIME_FORMAT.DATE_JP_TIME}
                      showTime={customTimeConfig(
                        form.getFieldValue("completion_end_at"),
                      )}
                      disabled={isCompletionEndDisabled}
                      allowClear
                      disabledDate={(current) =>
                        current && current < moment().startOf("day")
                      }
                      onChange={(value) =>
                        handleDateChange(value, "completion_end_at", (val) => {
                          const endDateTime =
                            form.getFieldValue("maintenance_end_at");
                          return (
                            val &&
                            endDateTime &&
                            val.isSameOrBefore(endDateTime, "minute")
                          );
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </StylesModalForm>
      </WrapperDetail>
    </StyleMaintenanceNotificationDetail>
  );
};

export default ModalForm;
